import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import { CameraOutlined, DeleteOutlined } from '@ant-design/icons';
import { message, Table, Button, Popconfirm, Tag, Alert, Select, Input } from 'antd';
import axiosInstance from '../../utils/axios';
import { dateFormat } from '../../utils/dateFormats';
import GalleryModal from './GalleryModal';

// ✅ Editable cell for "Link" column
const EditableLinkCell = ({ record, refresh, page }) => {
  const [editing, setEditing] = useState(false);
  const [newLink, setNewLink] = useState(record.link || '');
  const [saving, setSaving] = useState(false);

  const saveLink = async () => {
    if (newLink === record.link) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.patch(`gallery/gallery/${record.id}/`, { link: newLink });
      message.success('Link updated successfully');
      setEditing(false);
      refresh(`gallery/gallery/?page=${page}`);
    } catch (error) {
      message.error('Failed to update link');
    } finally {
      setSaving(false);
    }
  };

  return editing ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Input
        size="small"
        value={newLink}
        onChange={(e) => setNewLink(e.target.value)}
        style={{ width: 180 }}
        disabled={saving}
      />
      <Button type="link" size="small" onClick={saveLink} loading={saving}>
        OK
      </Button>
      <Button type="link" size="small" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </div>
  ) : (
    <div
      style={{ cursor: 'pointer', color: '#1677ff' }}
      onClick={() => setEditing(true)}
    >
      {record.link ? record.link : <span style={{ color: '#999' }}>Click to add</span>}
    </div>
  );
};

const Gallery = () => {
  const [medias, setMedias] = useState({ page: 1, results: [] });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let url = `gallery/gallery/?page=${page}`;
    if (filter === 'banner') url += `&is_banner=true`;
    else if (filter === 'banner_bg') url += `&is_banner_bg=true`;
    fetchData(url);
  }, [page, filter]);

  const fetchData = async (url) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(url);
      setMedias(response.data);
    } catch (error) {
      message.error('Failed to fetch gallery');
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (id, status) => {
    setLoading(true);
    try {
      await axiosInstance.patch(`gallery/gallery/${id}/`, { publish: status });
      fetchData(`gallery/gallery/?page=${page}`);
    } catch (error) {
      message.error('Cannot change status');
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => message.info('Cancelled');

  const confirm = async (id) => {
    try {
      await axiosInstance.delete(`gallery/gallery/${id}/`);
      message.success('Deleted successfully');
      setPage(1);
      setFilter('');
      fetchData(`gallery/gallery/?page=${1}`);
    } catch (err) {
      message.error('Cannot delete');
    }
  };

  const columns = [
    {
      title: 'Media URL',
      dataIndex: 'media',
      key: 'media',
      render: (_, record) => {
        const mediaUrl =
          record.image || record.video || record.banner || record.banner_bg;
        if (!mediaUrl) return <span>—</span>;
        const isVideo = !!record.video;
        return (
          <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
            {isVideo ? 'View Video' : 'View Image'}
          </a>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (_, record) => (
        <EditableLinkCell record={record} refresh={fetchData} page={page} />
      ),
    },
    {
      title: 'Usage',
      dataIndex: 'usage',
      key: 'usage',
      render: (_, record) => {
        if (record.is_banner) {
          return <Tag color="blue">Banner</Tag>;
        } else if (record.is_banner_bg) {
          return <Tag color="purple">Banner BG</Tag>;
        } else {
          return <Tag color="default">Gallery</Tag>;
        }
      },
    },
    {
      title: 'Status (Editable)',
      dataIndex: 'publish',
      key: 'publish',
      render: (_, record) =>
        record.publish ? (
          <Tag
            style={{ cursor: 'pointer' }}
            color="success"
            onClick={() => changeStatus(record.id, false)}
          >
            Published
          </Tag>
        ) : (
          <Tag
            style={{ cursor: 'pointer' }}
            color="error"
            onClick={() => changeStatus(record.id, true)}
          >
            Hidden
          </Tag>
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => dateFormat(text),
    },
    {
      title: 'Action',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Delete"
          description="Are you sure to delete this item?"
          onConfirm={() => confirm(record.id)}
          onCancel={cancel}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <Alert
        message="Media Cleanup Recommended"
        description="Keeping unused images and videos can slow down the system and increase storage costs. Please delete any media files that are no longer needed to ensure smooth performance and efficient resource usage."
        type="warning"
        showIcon
      />
      <br />
      <PageHeading name="Gallery" icon={<CameraOutlined />} />
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Select
          placeholder="Filter by usage"
          allowClear
          style={{ width: 200 }}
          onChange={(value) => {
            setPage(1);
            setFilter(value);
          }}
        >
          <Select.Option value="">All</Select.Option>
          <Select.Option value="banner">Banner</Select.Option>
          <Select.Option value="banner_bg">Banner BG</Select.Option>
        </Select>
        <Button type="primary" onClick={() => setCreateModalOpen(true)}>
          Add new
        </Button>
      </div>
      <Table
        dataSource={medias.results}
        loading={loading}
        columns={columns}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: 20,
          total: medias.count,
          onChange: (newPage) => {
            setPage(newPage);
          },
        }}
      />
      <GalleryModal open={createModalOpen} setOpen={setCreateModalOpen} refresh={fetchData} />
    </div>
  );
};

export default Gallery;
